import { normalizeWhatsAppNumber } from '@arabat/contracts';
import { useState } from 'react';
import { downloadBlob } from '../download.js';
import { invoicePdfFile } from './invoice-document.js';

/*
 * The store's invoice actions (ADR-0138 amendment 7): a chat with the customer in the store's own
 * WhatsApp with the invoice link written (a), the PDF through the device's share sheet (b), and a
 * call. WhatsApp offers no way to open one customer's chat with a file attached, hence two.
 */

/** wa.me opens the customer's chat with the text typed; null without a usable number. */
export function customerChatUrl(phone: string | null, text: string): string | null {
  const number = phone ? normalizeWhatsAppNumber(phone) : null;
  return number ? `https://wa.me/${number}?text=${encodeURIComponent(text)}` : null;
}

export function customerCallUrl(phone: string | null): string | null {
  const number = phone ? normalizeWhatsAppNumber(phone) : null;
  return number ? `tel:+${number}` : null;
}

type ShareState = 'idle' | 'preparing' | 'ready';
export type ShareOutcome = 'shared' | 'cancelled' | 'downloaded' | 'tap-again';

/**
 * Shares the invoice PDF. Drawing the PDF takes a moment and browsers only open the share sheet
 * right after a tap: when the first tap has expired, the file is kept and the next tap shares
 * at once. Browsers that cannot share files download it instead.
 */
export function useInvoiceShare(html: () => Promise<string>, fileName: string, text: string) {
  const [file, setFile] = useState<File | null>(null);
  const [state, setState] = useState<ShareState>('idle');
  const share = async (): Promise<ShareOutcome> => {
    let pdf = file;
    if (!pdf) {
      setState('preparing');
      try {
        pdf = await invoicePdfFile(await html(), fileName);
      } catch (error) {
        setState('idle');
        throw error;
      }
      setFile(pdf);
    }
    setState('ready');
    if (!navigator.canShare?.({ files: [pdf] })) {
      downloadBlob(pdf, pdf.name, 30_000);
      return 'downloaded';
    }
    try {
      await navigator.share({ files: [pdf], text });
      return 'shared';
    } catch (error) {
      const name = (error as Error).name;
      if (name === 'AbortError') return 'cancelled';
      if (name === 'NotAllowedError') return 'tap-again';
      throw error;
    }
  };
  return { share, preparing: state === 'preparing', ready: file !== null };
}
